import React, { useState } from 'react';
import { ChevronDownIcon, CalendarIcon, ClockIcon } from 'lucide-react';
import { CRESTMONT } from './CrestmontChrome';
import type { CrestmontProgram, CurriculumGroup } from '../../data/crestmontPrograms';
const TONE_STYLES: Record<CurriculumGroup['tone'], {
  background: string;
  text: string;
  badgeBg: string;
  badgeText: string;
}> = {
  navy: {
    background: CRESTMONT.navy,
    text: '#ffffff',
    badgeBg: '#ffffff',
    badgeText: CRESTMONT.navy
  },
  gold: {
    background: CRESTMONT.gold,
    text: CRESTMONT.ink,
    badgeBg: CRESTMONT.ink,
    badgeText: CRESTMONT.gold
  },
  ink: {
    background: CRESTMONT.ink,
    text: '#ffffff',
    badgeBg: CRESTMONT.gold,
    badgeText: CRESTMONT.ink
  }
};
function CurriculumPanel({
  group,
  isOpen,
  onToggle
}: {
  group: CurriculumGroup;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const tone = TONE_STYLES[group.tone];
  const panelId = `curriculum-${group.name.replace(/\s+/g, '-').toLowerCase()}`;
  return <div className="rounded-md overflow-hidden" style={{
    backgroundColor: tone.background,
    color: tone.text
  }} data-id="element-251" data-mp="2th15blxgohdd">
      <button type="button" onClick={onToggle} aria-expanded={isOpen} aria-controls={panelId} className="w-full text-left px-5 py-4 flex items-start gap-3.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/70" data-id="element-252" data-mp="2yfvleg41nrts">
        <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{
        backgroundColor: tone.badgeBg,
        color: tone.badgeText
      }} aria-hidden="true" data-id="element-253" data-mp="2ap8cpsmb0y66">
          {group.count}
        </span>
        <span className="flex-1 min-w-0" data-id="element-254" data-mp="25srmmsvjo9qg">
          <span className="block text-sm font-bold" data-id="element-255" data-mp="25pn934w6w1n4">{group.name}</span>
          <span className="block mt-1 text-xs leading-relaxed opacity-80" data-id="element-256" data-mp="1rsgwxt5z116v">
            {group.description}
          </span>
        </span>
        <ChevronDownIcon className={`mt-1 w-4 h-4 flex-shrink-0 opacity-70 transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-257" data-mp="2atg0ks2gjnt" />
      </button>
      {isOpen && <ul id={panelId} className="px-5 pb-5 space-y-1.5" data-id="element-258" data-mp="3or7drrcnziw9">
          {group.courses.map(course => <li key={course.code} className="flex items-baseline gap-2 text-xs leading-relaxed" data-id="element-259" data-mp="3kqdtd7yfkm84">
              <span className="font-bold tabular-nums opacity-90 w-[62px] flex-shrink-0" data-id="element-260" data-mp="gakz2zxfwq8o">
                {course.code}
              </span>
              <span className="flex-1 opacity-85" data-id="element-261" data-mp="c7y1sji02d43">{course.title}</span>
              <span className="opacity-60 tabular-nums" data-id="element-262" data-mp="7aft8u7w9zwm">{course.credits}</span>
            </li>)}
        </ul>}
    </div>;
}
export function CurriculumSection({
  program
}: {
  program: CrestmontProgram;
}) {
  const {
    curriculum
  } = program;
  const [openGroup, setOpenGroup] = useState<string>(curriculum.groups[0].name);
  return <section className="py-16 md:py-20" style={{
    backgroundColor: CRESTMONT.mist
  }} aria-labelledby="curriculum-heading" data-id="element-263" data-mp="17jnu0pi2i7ti">
      <div className="max-w-5xl mx-auto px-4" data-id="element-264" data-mp="1gj16lr1hncig">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.16em] text-[#00426b]/60" data-id="element-265" data-mp="1gh81ig6ix0ck">
          Curriculum
        </p>
        <h2 id="curriculum-heading" className="mt-2 text-center text-3xl md:text-4xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-266" data-mp="1q8ksz6oetoi2">
          {curriculum.heading}
        </h2>
        <p className="mt-3 text-center text-sm text-gray-600" data-id="element-267" data-mp="206ifnpqmfd8k">
          {curriculum.subheading}
        </p>

        <div className="mt-10 bg-white rounded-lg p-5 md:p-8" data-id="element-268" data-mp="247c0294uu9wp">
          <div className="grid gap-8 lg:grid-cols-2" data-id="element-269" data-mp="5t0n1obczq6o">
            {/* Course groups */}
            <div data-id="element-270" data-mp="9uchq893b1lq">
              <div className="flex items-center gap-2.5 pb-4" data-id="element-271" data-mp="9tsx2js21z40">
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                backgroundColor: CRESTMONT.navy
              }} data-id="element-272" data-mp="9wf84te5mwqz">
                  <ClockIcon className="w-4 h-4 text-white" aria-hidden="true" data-id="element-273" data-mp="36np0433edcz1" />
                </span>
                <span data-id="element-274" data-mp="3fod9gkhuip5l">
                  <span className="block text-sm font-extrabold tracking-wide" style={{
                  color: CRESTMONT.navy
                }} data-id="element-275" data-mp="3fp6bx99rushx">
                    {curriculum.startLabel}
                  </span>
                  <span className="block text-[11px] text-gray-500" data-id="element-276" data-mp="31ovji1std5un">
                    {curriculum.startCaption}
                  </span>
                </span>
              </div>
              <div className="space-y-3" data-id="element-277" data-mp="2ww2h3kkti9lq">
                {curriculum.groups.map(group => <CurriculumPanel key={group.name} group={group} isOpen={openGroup === group.name} onToggle={() => setOpenGroup(prev => prev === group.name ? '' : group.name)} data-id="element-278" data-mp="296ht2n9t4m5l" />)}
              </div>
            </div>

            {/* Pace and total */}
            <div data-id="element-279" data-mp="etdz7v5t57em">
              <div className="flex items-center gap-2.5 pb-4" data-id="element-280" data-mp="esvsg4lirasg">
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                backgroundColor: CRESTMONT.gold
              }} data-id="element-281" data-mp="12hou8hotocuz">
                  <CalendarIcon className="w-4 h-4" style={{
                  color: CRESTMONT.ink
                }} aria-hidden="true" data-id="element-282" data-mp="ths0i1nksnt2" />
                </span>
                <span data-id="element-283" data-mp="3e6y75pofkzkx">
                  <span className="block text-sm font-extrabold tracking-wide" style={{
                  color: CRESTMONT.navy
                }} data-id="element-284" data-mp="3e6ogs0zh4qep">
                    {curriculum.durationLabel}
                  </span>
                  <span className="block text-[11px] text-gray-500" data-id="element-285" data-mp="1os6sghtepsw4">
                    {curriculum.durationCaption}
                  </span>
                </span>
              </div>
              <div className="overflow-hidden rounded-md border border-gray-200" data-id="element-286" data-mp="1eyg4de16zkgy">
                <table className="w-full text-sm" data-id="element-287" data-mp="1ayforq2cx38b">
                  <caption className="sr-only" data-id="element-288" data-mp="160dvydf4ppjn">
                    Course and credit load by year for the {program.name}
                  </caption>
                  <thead style={{
                  backgroundColor: CRESTMONT.ink
                }} data-id="element-289" data-mp="10zgey45uv1jq">
                    <tr className="text-white" data-id="element-290" data-mp="w2q17nhiwi1h">
                      <th scope="col" className="px-4 py-2.5 text-left text-[11px] font-bold uppercase tracking-wider" data-id="element-291" data-mp="w3hwex77kdr8">
                        Period
                      </th>
                      <th scope="col" className="px-4 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider" data-id="element-292" data-mp="2mf5p4sk3n0u0">
                        Courses
                      </th>
                      <th scope="col" className="px-4 py-2.5 text-right text-[11px] font-bold uppercase tracking-wider" data-id="element-293" data-mp="d33dlpl7q8si">
                        Credits
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white" data-id="element-294" data-mp="3lf7xm2ew5ce2">
                    {curriculum.terms.map(row => <tr key={row.term} data-id="element-295" data-mp="3ud1p901pnhuw">
                        <td className="px-4 py-2.5 text-gray-700" data-id="element-296" data-mp="3uc05m76mpn1k">
                          {row.term}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700" data-id="element-297" data-mp="19ncdwx07ue6y">
                          {row.courses}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700" data-id="element-298" data-mp="1ekub5q4vf4z4">
                          {row.credits}
                        </td>
                      </tr>)}
                  </tbody>
                  <tfoot data-id="element-299" data-mp="l8xmqgh73mss">
                    <tr style={{
                    backgroundColor: CRESTMONT.gold
                  }} data-id="element-300" data-mp="z2oylviefiro">
                      <td className="px-4 py-3 text-sm font-extrabold" style={{
                      color: CRESTMONT.ink
                    }} data-id="element-301" data-mp="z4035xjo4uju">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-extrabold tabular-nums" style={{
                      color: CRESTMONT.ink
                    }} data-id="element-302" data-mp="1421j49lssdr3">
                        {curriculum.totalCourses}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-extrabold tabular-nums" style={{
                      color: CRESTMONT.ink
                    }} data-id="element-303" data-mp="3jse7zt0c2ccz">
                        {curriculum.totalCredits}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-gray-500" data-id="element-304" data-mp="39no2xe9mus4j">
                Standard plan shown. Transfer credit and Prior Learning
                Assessment reduce both the course count and the total cost.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
export function SpecializationsSection({
  program
}: {
  program: CrestmontProgram;
}) {
  const [activeId, setActiveId] = useState(program.specializations[0].id);
  const active = program.specializations.find(s => s.id === activeId) || program.specializations[0];
  return <section className="py-16 md:py-20 bg-white" aria-labelledby="spec-heading" data-id="element-305" data-mp="2hzdysgm79912">
      <div className="max-w-5xl mx-auto px-4" data-id="element-306" data-mp="b7d57cjh6wdy">
        <h2 id="spec-heading" className="text-center text-3xl md:text-4xl font-extrabold tracking-tight" style={{
        color: CRESTMONT.navy
      }} data-id="element-307" data-mp="g5n0ro943z03">
          Specializations
        </h2>
        <p className="mt-3 text-center text-sm text-gray-600" data-id="element-308" data-mp="28e33fsr9o8li">
          Choose one concentration. It defines the elective half of your degree.
        </p>

        <div className="mt-8 flex flex-wrap justify-center gap-2.5" role="tablist" aria-label="Specializations" data-id="element-309" data-mp="1uklkoa6emzwj">
          {program.specializations.map(spec => {
          const isActive = spec.id === active.id;
          return <button key={spec.id} role="tab" aria-selected={isActive} aria-controls={`spec-panel-${spec.id}`} id={`spec-tab-${spec.id}`} onClick={() => setActiveId(spec.id)} className="px-4 py-2 rounded-full text-xs font-bold border transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#00426b]" style={isActive ? {
            backgroundColor: CRESTMONT.navy,
            borderColor: CRESTMONT.navy,
            color: '#ffffff'
          } : {
            backgroundColor: '#ffffff',
            borderColor: '#d4d9df',
            color: CRESTMONT.navy
          }} data-id="element-310" data-mp="2rbw8b0lgaslj">
                {spec.name}
              </button>;
        })}
        </div>

        <div id={`spec-panel-${active.id}`} role="tabpanel" aria-labelledby={`spec-tab-${active.id}`} className="mt-8 rounded-lg p-5 md:p-7" style={{
        backgroundColor: CRESTMONT.mist
      }} data-id="element-311" data-mp="pf58ncl5nixr">
          <div className="grid gap-6 md:grid-cols-3" data-id="element-312" data-mp="3t2xgefhj1ltj">
            <div data-id="element-313" data-mp="3o55h30ejyxfb">
              <h3 className="text-lg font-bold" style={{
              color: CRESTMONT.navy
            }} data-id="element-314" data-mp="3o5ng24slnpqx">
                {active.name}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-gray-700" data-id="element-315" data-mp="1x13qet0e417g">
                {active.summary}
              </p>
              {active.note && <div className="mt-4 bg-white rounded p-3.5 border-l-2 border-[#fcc23f]" data-id="element-316" data-mp="21sc0lr9jnqlo">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500" data-id="element-317" data-mp="25sw031hlb6az">
                    Worth knowing
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-gray-600" data-id="element-318" data-mp="1e1y9c2ys9vs5">
                    {active.note}
                  </p>
                </div>}
            </div>
            <div className="bg-white rounded p-5" data-id="element-319" data-mp="1i65x9xxf37pb">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500" data-id="element-320" data-mp="1n3pdv4hl7wkf">
                Core courses
              </h4>
              <ul className="mt-3 space-y-2" data-id="element-321" data-mp="2tlcgl7loiq86">
                {active.coreCourses.map(course => <li key={course} className="text-xs leading-relaxed text-gray-700" data-id="element-322" data-mp="2yjw3ba83k61l">
                    {course}
                  </li>)}
              </ul>
            </div>
            <div className="bg-white rounded p-5" data-id="element-323" data-mp="2yuvqx4138g3u">
              <h4 className="text-[11px] font-bold uppercase tracking-[0.12em] text-gray-500" data-id="element-324" data-mp="2uulasyjl09b9">
                Career outcomes
              </h4>
              <ul className="mt-3 space-y-2" data-id="element-325" data-mp="2pwbdoieaqyn0">
                {active.careerOutcomes.map(outcome => <li key={outcome} className="flex items-start gap-2 text-xs leading-relaxed text-gray-700" data-id="element-326" data-mp="2kvnlhtec0br7">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{
                  backgroundColor: CRESTMONT.gold
                }} aria-hidden="true" data-id="element-327" data-mp="2bw92tg4ep5h9" />
                    {outcome}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>;
}